import { motion, type Variants } from 'framer-motion';
import type { ReactNode } from 'react';

/**
 * Shared gentle micro-interactions (framer-motion).
 * Springs, 150–300ms feel, no bouncy excess.
 */

const spring = { type: 'spring', stiffness: 380, damping: 32, mass: 0.8 } as const;

export const fadeSlideVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: spring },
};

export const staggerListVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05, delayChildren: 0.02 } },
};

const listVariants = staggerListVariants;

interface MotionProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}

/** Single element: soft fade + slight rise on mount. */
export function FadeIn({ children, className, delay = 0 }: MotionProps) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...spring, delay }}
    >
      {children}
    </motion.div>
  );
}

/** List container: staggers its <ListItem> children. */
export function StaggerList({ children, className }: MotionProps) {
  return (
    <motion.div className={className} variants={listVariants} initial="hidden" animate="visible">
      {children}
    </motion.div>
  );
}

/** Child of <StaggerList> (or standalone fade/slide item). */
export function ListItem({ children, className }: MotionProps) {
  return (
    <motion.div className={className} variants={fadeSlideVariants}>
      {children}
    </motion.div>
  );
}
